import { Link, useParams } from "react-router";
import { Empty, SkeletonLines, Sigil, Stat, TxLink } from "../components/ui";
import { agentStage, parseId, usePurchase, usePurchasePrompt, useSystemPrompt, type Agent, type PublicData } from "../lib/queries";
import { bytes, isoDate, pad, usdc } from "../lib/format";
import { explorer, publicApiOrigin } from "../config";
import { PromptTerminal } from "../components/PromptTerminal";
import type { PurchasePrompt, SystemPrompt } from "../lib/public-v2";
import { PUBLIC_V2_CONFIG } from "../lib/public-v2";
import { RegistryCreditWithdrawal } from "../components/RegistryCreditWithdrawal";

function isoMillis(value: string): string {
  try { const milliseconds = BigInt(value); return milliseconds === 0n ? "Not attested" : isoDate((milliseconds / 1000n).toString()); } catch { return "—"; }
}
function isoRecordedSeconds(value: string): string { return value === "0" ? "Not attested" : isoDate(value); }

function promptDescription(purchase: PublicData<Agent>, prompt: PublicData<PurchasePrompt>): string {
  if (purchase.state !== "data" || !purchase.data) return "Prompt unavailable";
  const row = purchase.data;
  if (!row.promptFinalized) return `Prompt upload in progress: ${row.promptChunksReceived}/${row.promptChunks} chunks.`;
  if (prompt.state === "failed" || prompt.state === "not-found") return "Current prompt could not be validated; purchase-row text is not shown as current.";
  if (prompt.state !== "data" || !prompt.data) return "Current prompt is loading or unavailable.";
  const current = prompt.data;
  if (current.agentId !== row.agentId || current.encoding !== row.promptEncoding || current.chunkCount !== row.promptChunks
    || current.chunksReceived !== row.promptChunksReceived || current.root !== row.promptRoot || current.encodedDigest !== row.encodedPromptDigest
    || current.decodedDigest !== row.decodedPromptDigest || current.encodedBytes !== row.encodedPromptBytes || current.decodedBytes !== row.decodedPromptBytes) {
    return "Prompt commitments do not match this purchase.";
  }
  if (current.decodeError) return `Prompt decode error: ${current.decodeError}`;
  if (current.text !== null) return current.text || "No additional prompt — genesis only.";
  if (current.displayText !== null) return current.displayText;
  return "Prompt text is unavailable.";
}

export function describeMatchingGenesis(genesis: SystemPrompt | null): string | null {
  if (!genesis) return null;
  if (genesis.decodeError) return `Decode error: ${genesis.decodeError}`;
  if (genesis.chunkCount === 0 && genesis.text === "" && genesis.decodedBytes === 0) return "Validated empty system prompt";
  return genesis.text ?? genesis.displayText ?? "Genesis text is unavailable.";
}

export function AgentPage() {
  const parsed = parseId(useParams().id);
  const id = parsed?.toString() ?? null;
  const query = usePurchase(id);
  const prompt = usePurchasePrompt(id);
  const system = useSystemPrompt(parsed !== null);
  if (parsed === null) return <Empty>Not a canonical purchase id.</Empty>;
  if (query.state === "loading") return <SkeletonLines lines={6} />;
  if (query.state === "unavailable") return <Empty>Public Registry V2 data is not configured.</Empty>;
  if (query.state === "failed") return <Empty>Purchase data is unavailable or invalid.</Empty>;
  if (query.state === "not-found") return <Empty>Purchase #{parsed.toString()} was not found.</Empty>;
  if (query.state !== "data" || !query.data) return <Empty>Purchase data is unavailable.</Empty>;

  const purchase = query.data;
  const stage = agentStage(purchase);
  const systemPrompt = system.state === "data" ? system.data : null;
  const boundGenesis = systemPrompt?.systemPromptHash === purchase.systemPromptHash ? systemPrompt : null;
  const promptPath = publicApiOrigin ? `${publicApiOrigin}/v2/purchases/${encodeURIComponent(purchase.agentId)}/prompt` : null;
  const promptText = promptDescription(query, prompt);
  let genesisText: string;
  const matchedGenesisText = describeMatchingGenesis(boundGenesis);
  if (matchedGenesisText !== null) genesisText = matchedGenesisText;
  else if (system.state === "unavailable") genesisText = "Public system prompt unavailable.";
  else if (system.state === "failed") genesisText = "Public system prompt could not be validated.";
  else if (system.state === "not-found") genesisText = "No finalized system prompt is available.";
  else genesisText = "Genesis is unavailable or has a different systemPromptHash.";

  return <>
    <div className="spread" style={{ marginBottom: 24 }}>
      <div className="agent-id"><Sigil id={purchase.agentId} size={72} /><div>
        <h1 style={{ fontSize: "clamp(28px,4vw,44px)" }}>{purchase.metadataComplete && purchase.name ? purchase.name : "Metadata incomplete"}</h1>
        <div className="row small muted"><span>purchase #{pad(purchase.agentId)}</span><span>·</span><span>{stage.label}</span></div>
      </div></div>
      <div className="row small">{explorer && <TxLink hash={purchase.paidTransactionHash}>payment tx</TxLink>}{promptPath && <a href={promptPath} target="_blank" rel="noreferrer">prompt JSON ↗</a>}</div>
    </div>
    <div className="notice">On-chain state only. A readiness attestation does not prove a service is running.</div>
    <div className="stats" style={{ marginTop: 20 }}>
      <Stat label="Phase" value={stage.label} />
      <Stat label="Paid" value={usdc(purchase.price)} sub={`tier ${purchase.tierId}`} />
      <Stat label="Creator" value={<span title={purchase.creator}>{purchase.creator}</span>} />
      <Stat label="Payer" value={<span title={purchase.payer}>{purchase.payer}</span>} />
      <Stat label="Account" value={<span title={purchase.account}>{purchase.account}</span>} />
      <Stat label="Prompt" value={purchase.promptFinalized ? bytes(purchase.decodedPromptBytes) : `${purchase.promptChunksReceived}/${purchase.promptChunks}`} sub={purchase.promptFinalized ? "prompt finalization attested" : "chunks received"} />
      <Stat label="Authorized" value={purchase.authorized ? "yes" : "no"} />
      <Stat label="Session revoked" value={purchase.sessionRevoked ? "yes" : "no"} />
      <Stat label="Refund credited" value={purchase.refundCredited ? "yes" : "no"} />
      <Stat label="Settlement credited" value={purchase.settlementCredited ? "yes" : "no"} />
      <Stat label="Identity delivered" value={purchase.identityDelivered ? "yes" : "no"} />
      <Stat label="Capacity released" value={purchase.capacityReleased ? "yes" : "no"} />
    </div>
    {PUBLIC_V2_CONFIG.kind === "configured" && <div style={{ marginTop: 24 }}><RegistryCreditWithdrawal purchase={purchase} deployment={PUBLIC_V2_CONFIG.reference} /></div>}
    <div className="split" style={{ marginTop: 24 }}>
      <PromptTerminal url={promptPath ? `/v2/purchases/${purchase.agentId}/prompt` : "public prompt unavailable"}
        tabs={[{ id: "purchase", label: "purchase prompt", content: <span className="prompt-agent">{promptText}</span> }, { id: "genesis", label: "matching genesis", content: <span className="prompt-sys">{genesisText}</span> }]}
        footer={<dl className="kv"><dt>prompt root</dt><dd className="hash">{purchase.promptRoot}</dd><dt>system prompt hash</dt><dd className="hash">{purchase.systemPromptHash}</dd><dt>paid at</dt><dd>{isoDate(purchase.paidAt)}</dd></dl>} />
      <div className="panel panel-pad stack"><h3>Public record</h3>
        <div><span className="label">Reservation</span><div className="hash">{purchase.reservationId}</div></div>
        <div><span className="label">Capacity slot / epoch</span><div className="hash">{purchase.capacitySlotId} · {purchase.slotEpoch}</div></div>
        <div><span className="label">Evidence hashes</span><div className="hash">Readiness: {purchase.readinessEvidenceHash}</div><div className="hash">Cleanup receipt: {purchase.cleanupReceipt} · epoch {purchase.cleanupReceiptEpoch}</div></div>
        <div><span className="label">Host interval</span><div>{isoMillis(purchase.hostStartedAtMs)} → {isoMillis(purchase.hostExpiresAtMs)}</div></div>
        <div><span className="label">Session interval</span><div>{isoRecordedSeconds(purchase.sessionStartedAt)} → {isoRecordedSeconds(purchase.sessionExpiresAt)}</div></div>
        <div><span className="label">Economic expiry</span><div>{isoRecordedSeconds(purchase.economicExpirySeconds)} · settle before {isoMillis(purchase.settleBeforeMs)}</div></div>
        <p className="small muted">Credits, cleanup receipts and capacity flags are indexed attestations; they do not prove cash withdrawal, physical purge or current quota. A readiness attestation does not establish that a service is running.</p>
        <Link to="/">← Explore purchases</Link>
      </div>
    </div>
  </>;
}
